import { type FunctionComponent } from 'react';
import type { ImageFieldsFragment, TextStyle } from '../../../__generated__/graphql-client-types';
import { PLACEHOLDER_OPTIONS } from '../../../constants/cloudinary/general';
import { EXCLUSIVE_DEALS_IMAGE_CLOUDINARY_OPTIONS, EXCLUSIVE_DEALS_TEXT_STYLES } from '../../../constants/home/general';
import { findMaxCardWidth } from '../../../helpers/home/home-carousel/home-carousel.helpers';
import { type CarouselItemContent, type LinkContent } from '../../../types/construct.types';
import { Link } from '../../common-components/link/link.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import {
	CarouselCardOptionWithSubtitle,
	HomeCarouselOptionCard
} from '../home-carousel/home-carousel-option-card/home-carousel-option-card.component';
import { findExclusiveDealsArtworkImageCloudinaryOptions } from '../home-common.helpers';

type ResponsiveArtworkImageProps = {
	isNotSmall: boolean;
	responsiveClassName?: string;
	artworkImage: ImageFieldsFragment;
	itemsCount: number;
	className?: string;
};

const ResponsiveArtworkImage: FunctionComponent<ResponsiveArtworkImageProps> = ({
	isNotSmall,
	responsiveClassName,
	artworkImage,
	itemsCount,
	className
}) => (
	<CloudinaryImage
		{...artworkImage}
		className={`${className} ${responsiveClassName}`}
		options={findExclusiveDealsArtworkImageCloudinaryOptions(itemsCount, isNotSmall)}
		useDimensions={false}
		publicID={artworkImage.id}
		placeholderOptions={{
			...findExclusiveDealsArtworkImageCloudinaryOptions(itemsCount, isNotSmall),
			...PLACEHOLDER_OPTIONS
		}}
	/>
);

type ArtworkImageProps = {
	artworkImage: ImageFieldsFragment;
	itemsCount: number;
	label: string;
	link: LinkContent;
	className?: string;
};

export const ArtworkImage: FunctionComponent<ArtworkImageProps> = ({
	artworkImage,
	itemsCount,
	link,
	label,
	className = 'br2 br--top w-100'
}) => {
	const { linkUrl, trackingCode } = link;

	return (
		<Link url={linkUrl} analyticsHook={trackingCode ?? undefined} ariaLabel={label} className="flex">
			<ResponsiveArtworkImage
				artworkImage={artworkImage}
				itemsCount={itemsCount}
				className={className}
				isNotSmall={false}
				responsiveClassName="db dn-ns"
			/>
			<ResponsiveArtworkImage
				artworkImage={artworkImage}
				itemsCount={itemsCount}
				className={className}
				isNotSmall={true}
				responsiveClassName="dn db-ns"
			/>
		</Link>
	);
};

const FONT_COLOR_MAP: Record<string, TextStyle> = {
	black: 'BLACK',
	white: 'WHITE',
	primary: 'PRIMARY',
	secondary: 'SECONDARY',
	dark_grey: 'DARK_GREY',
	'grey-dark': 'DARK_GREY'
};

const resolveTextStyle = (value?: string): TextStyle => {
	return FONT_COLOR_MAP[value?.toLowerCase() ?? 'black'] ?? 'BLACK';
};

type CarouselItemContentWithArtworkImage = {
	fields: {
		artwork: {
			image: ImageFieldsFragment;
		};
	};
} & CarouselItemContent;

function isExclusiveDealsItem(item: CarouselItemContent): item is CarouselItemContentWithArtworkImage {
	return !!(
		'fields' in item &&
		'artwork' in item.fields &&
		item.fields.artwork &&
		'image' in item.fields.artwork &&
		item.fields.artwork.image
	);
}

export type HomeExclusiveDealsCardOptionProps = {
	item: CarouselItemContent;
	itemsCount?: number;
};
export const HomeExclusiveDealsCardOption: FunctionComponent<HomeExclusiveDealsCardOptionProps> = ({ item, itemsCount }) => {
	if (!isExclusiveDealsItem(item) || !itemsCount) {
		return null;
	}
	const {
		artwork: { image: artworkImage },
		link
	} = item.fields;
	const { linkUrl, trackingCode } = link;
	const isSingleDisplay = itemsCount === 1;
	const title = item.fields.title ?? '';
	const subtitle = item.fields.subTitle;
	const textStyle = resolveTextStyle(item.fields.fontColor);
	return (
		<div className={`w-100 flex flex-column items-center`} style={{ maxWidth: findMaxCardWidth(itemsCount), minWidth: 'auto' }}>
			<div className="relative flex flex-column items-center justify-between w-100">
				{isSingleDisplay ? (
					<div className="db dn-ns w-100">
						<ArtworkImage artworkImage={artworkImage} itemsCount={itemsCount} link={link} label={title} />
					</div>
				) : (
					<ArtworkImage artworkImage={artworkImage} itemsCount={itemsCount} link={link} label={title} />
				)}
				<HomeCarouselOptionCard
					item={item}
					imageClassName={`br2 ${isSingleDisplay ? 'br--top-ns' : 'br--bottom'}`}
					isSingleDisplay={isSingleDisplay}
					cloudinaryOptions={EXCLUSIVE_DEALS_IMAGE_CLOUDINARY_OPTIONS}
				/>
				{isSingleDisplay ? (
					<div className="db dn-ns">
						<Link url={linkUrl} analyticsHook={trackingCode ?? undefined} ariaLabel={title} className="flex">
							<CarouselCardOptionWithSubtitle
								title={title}
								subTitle={subtitle}
								containerClassName={`flex flex-column items-center justify-between pa2 w-100 theme-${
									EXCLUSIVE_DEALS_TEXT_STYLES[isSingleDisplay ? 'BLACK' : textStyle]
								}`}
								titleClassName={'f4 f3-ns font-medium lh-title'}
								subtitleClassName={'mt0 mt2-l'}
							/>
						</Link>
					</div>
				) : (
					<Link url={linkUrl} analyticsHook={trackingCode ?? undefined} ariaLabel={item.fields.title} className="flex">
						<CarouselCardOptionWithSubtitle
							title={title}
							subTitle={subtitle}
							containerClassName={`flex flex-column items-center justify-between pa2 w-100 theme-${
								EXCLUSIVE_DEALS_TEXT_STYLES[isSingleDisplay ? 'BLACK' : textStyle]
							}`}
							titleClassName={'f4 f3-ns font-medium lh-title'}
							subtitleClassName={'mt0 mt2-l'}
						/>
					</Link>
				)}
				{isSingleDisplay && (
					<div className="dn db-ns">
						<Link url={linkUrl} analyticsHook={trackingCode ?? undefined} ariaLabel={title} className="flex">
							<CarouselCardOptionWithSubtitle
								title={title}
								subTitle={subtitle}
								containerClassName={`absolute z-1 bottom-0 right-0 flex flex-column items-center pv4 pv5-l mb2-l ph0 w-50 theme-${EXCLUSIVE_DEALS_TEXT_STYLES[textStyle]}`}
								titleClassName="f4 f3-ns font-medium lh-title"
								subtitleClassName="mt0 mt2-l"
							/>
						</Link>
						<ArtworkImage
							artworkImage={artworkImage}
							itemsCount={itemsCount}
							link={link}
							label={title}
							className="flex flex-column items-center justify-between w-100 br2 br--bottom"
						/>
					</div>
				)}
			</div>
		</div>
	);
};
